import type { GetIndicatorsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { LabelledIdArray } from 'src/rbac/types';

import type { CollectionData } from '@/utils/collectionUtils';

export type IndicatorFlatFields = CollectionData<
  GetIndicatorsQuery['indicator'][0]
>;
export enum ConformanceIndicatorRating {
  NotSet = 0,
  Outside = 1,
  OutsideAppetite = 3,
  Within = 2,
}

export enum ConformanceTrend {
  Deteriorating = 'deteriorating',
  Improving = 'improving',
  Stable = 'stable',
}

export type TestScheduleStatus = '-' | 'due' | 'overdue';
export type TestScheduleStatusLabelled = string;

/**
 * The three-way state of the Three Month Average column for one indicator row.
 *
 * Stored alongside `ThreeMonthAverageValue` (the numeric result, or `null`
 * for the two non-numeric states) so the table machinery can:
 *   - render the display string (`ThreeMonthAverage`),
 *   - sort numerically by the raw value (`ThreeMonthAverageValue`),
 *   - and switch behaviour on the cell tooltip / CSV-export path via the
 *     state (e.g. `empty` exports as `''`, `na` does not).
 *
 * The split exists because `null` on `ThreeMonthAverageValue` conflates the
 * two non-numeric states — keep both fields when reading row data.
 */
export type ThreeMonthAverageState = 'empty' | 'na' | 'value';

export type IndicatorTableFields = IndicatorFlatFields & {
  CreatedByUserName: string | undefined;
  ModifiedByUserName: string | undefined;
  Conformance: ConformanceIndicatorRating;
  ConformanceLabelled: number | string | undefined;
  ConformanceTrend: string | undefined;
  ConformanceTrendValue: ConformanceTrend | null;
  LatestResultLabelled: string | undefined;
  PreviousResultLabelled: string | undefined;
  LatestResultDateLabelled: string | undefined;
  TestFrequencyLabelled: string | undefined;
  ParentTitle: string | undefined;
  ParentType: string | undefined;
  SequentialIdLabel: null | string;
  ThreeMonthAverage: string;
  ThreeMonthAverageState: ThreeMonthAverageState;
  ThreeMonthAverageValue: number | null;
  allOwners: LabelledIdArray;
  allContributors: LabelledIdArray;
  allViewers: LabelledIdArray;
  NextTestOverdueDate: string | undefined;
  NextTestDate: string | undefined;
  TestScheduleStatus: TestScheduleStatus;
  TestScheduleStatusLabelled: TestScheduleStatusLabelled;
};
